/**
 * Vue composables for Memory Intelligence
 */

import { inject, ref, InjectionKey } from "vue";
import type { MemoryIntelligenceClient, IntelligenceResponse } from "../../core/client.js";
import type {
  PatternAnalysis,
  TagSuggestionsResult,
  RelatedMemoriesResult,
  DuplicatesResult,
  InsightsResult,
  MemoryHealth,
} from "../../core/types.js";
import type {
  PredictiveRecallResult,
} from "../../core/prediction-types.js";

export const MEMORY_INTELLIGENCE_KEY: InjectionKey<MemoryIntelligenceClient> =
  Symbol("memoryIntelligence");

export function useMemoryIntelligence() {
  const client = inject(MEMORY_INTELLIGENCE_KEY);
  if (!client) {
    throw new Error("MemoryIntelligence client not provided");
  }
  return { client };
}

interface UseAsyncState {
  data: any;
  loading: any;
  error: any;
  execute: (...args: any[]) => Promise<void>;
  reset: () => void;
}

function useAsync<T>(
  asyncFunction: (...args: any[]) => Promise<T>
): UseAsyncState {
  const data = ref<T | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  const execute = async (...args: any[]) => {
    loading.value = true;
    error.value = null;
    try {
      const result = await asyncFunction(...args);
      data.value = result as any;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  };

  const reset = () => {
    data.value = null;
    error.value = null;
    loading.value = false;
  };

  return { data, loading, error, execute, reset };
}

export function usePatternAnalysis() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<PatternAnalysis>>((params) => client.analyzePatterns(params));
}

export function useTagSuggestions() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<TagSuggestionsResult>>((params) => client.suggestTags(params));
}

export function useRelatedMemories() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<RelatedMemoriesResult>>((params) => client.findRelated(params));
}

export function useDuplicateDetection() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<DuplicatesResult>>((params) => client.detectDuplicates(params));
}

export function useInsightExtraction() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<InsightsResult>>((params) => client.extractInsights(params));
}

export function useHealthCheck() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<MemoryHealth>>((params) => client.healthCheck(params));
}

/**
 * Predictive memory recall composable
 *
 * AI-powered prediction of memories you'll need based on your current context.
 *
 * @example
 * ```vue
 * <script setup>
 * import { usePredictiveRecall } from '@lanonasis/mem-intel-sdk/vue';
 *
 * const { data, loading, error, execute } = usePredictiveRecall();
 *
 * // Trigger predictions
 * const getPredictions = () => {
 *   execute({
 *     userId: 'user-123',
 *     context: {
 *       currentProject: 'Building dashboard',
 *       recentTopics: ['React', 'performance']
 *     },
 *     limit: 5
 *   });
 * };
 * </script>
 *
 * <template>
 *   <div v-if="loading">Loading predictions...</div>
 *   <div v-else-if="data">
 *     <div v-for="pred in data.data.predictions" :key="pred.id">
 *       <h3>{{ pred.title }} ({{ pred.confidence }}%)</h3>
 *       <p>{{ pred.reason }}</p>
 *     </div>
 *   </div>
 * </template>
 * ```
 */
export function usePredictiveRecall() {
  const { client } = useMemoryIntelligence();
  return useAsync<IntelligenceResponse<PredictiveRecallResult>>((params) => client.predictiveRecall(params));
}

/**
 * Record prediction feedback composable
 *
 * @example
 * ```vue
 * <script setup>
 * import { usePredictionFeedback } from '@lanonasis/mem-intel-sdk/vue';
 *
 * const { execute: recordFeedback, loading } = usePredictionFeedback();
 *
 * const handlePredictionClick = (memoryId: string) => {
 *   recordFeedback({
 *     memoryId,
 *     userId: 'user-123',
 *     useful: true,
 *     action: 'clicked'
 *   });
 * };
 * </script>
 * ```
 */
export function usePredictionFeedback() {
  const { client } = useMemoryIntelligence();
  return useAsync<void>((params) => client.recordPredictionFeedback(params));
}
